import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  catchError,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from "rxjs";
import { NoShowToastContext } from "src/app/core/interceptors/http-toast.interceptor";
import { ToastService } from "src/app/shared/services/toast.service";
import { environment } from "src/environments/environment";
import {
  buildUpdateProfilePayload,
  isUserOnboardingComplete,
  normalizeUserProfile,
  OnboardingConfig,
  serializeProfileUpdatePayload,
  UpdateProfileRequest,
  UpdateProfileResponse,
  UserProfile,
  UserProfileApiResponse,
} from "./profile.model";

export type {
  OnboardingConfig,
  ProfileViewModelState,
  UpdateProfileRequest,
  UpdateProfileResponse,
  UserProfile,
  UserProfileRole,
} from "./profile.model";

@Injectable({
  providedIn: "root",
})
export class ProfileService {
  private readonly API_ENDPOINT = "/user/me";
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly userProfile = signal<UserProfile | null>(null);

  /** Shared in-flight GET /user/me so route guards do not duplicate the request. */
  private inflightProfileRequest: Observable<UserProfile> | null = null;

  clearCache(): void {
    this.userProfile.set(null);
    this.inflightProfileRequest = null;
  }

  /** GET /user/me — onboarding flags come from the API (set at registration). */
  getUserProfile(): Observable<UserProfile> {
    const cached = this.userProfile();
    if (cached) {
      return of(cached);
    }

    if (!this.inflightProfileRequest) {
      this.inflightProfileRequest = this.fetchUserProfile().pipe(
        tap((profile) => this.userProfile.set(profile)),
        catchError((error) => {
          this.inflightProfileRequest = null;
          this.toast.error(
            this.translate.instant("toast_errors.getUserProfileFailed"),
          );
          console.error("Error fetching user profile:", error);
          return throwError(() => error);
        }),
        shareReplay(1),
      );
    }

    return this.inflightProfileRequest;
  }

  /** PUT /user/me */
  updateUserProfile(
    profileUpdate: UpdateProfileRequest,
    options?: { suppressSuccessToast?: boolean },
  ): Observable<UserProfile> {
    const payload = serializeProfileUpdatePayload(
      buildUpdateProfilePayload(profileUpdate, this.userProfile()),
    );

    return this.putUserProfile(payload, options).pipe(
      switchMap(() => this.fetchUserProfile()),
      tap((profile) => this.userProfile.set(profile)),
      catchError((error) => {
        this.toast.error(
          this.translate.instant("toast_errors.updateUserProfileFailed"),
        );
        console.error("Error updating user profile:", error);
        return throwError(() => error);
      }),
    );
  }

  markWelcomeScreenAsSeen(): Observable<UserProfile> {
    return this.patchOnboardingConfig(
      { hasSeenWelcomeScreen: true },
      { suppressSuccessToast: true },
    );
  }

  /** No-op (no request, no toast) when onboarding is already finished. */
  markOnboardingAsComplete(): Observable<UserProfile> {
    const currentProfile = this.userProfile();
    if (currentProfile && isUserOnboardingComplete(currentProfile)) {
      return of(currentProfile);
    }
    return this.patchOnboardingConfig({ isOnboardingIncomplete: false });
  }

  private fetchUserProfile(): Observable<UserProfile> {
    return this.http
      .get<UserProfileApiResponse>(
        `${environment.baseApiUrl}${this.API_ENDPOINT}`,
      )
      .pipe(map(normalizeUserProfile));
  }

  private putUserProfile(
    payload: ReturnType<typeof serializeProfileUpdatePayload>,
    options?: { suppressSuccessToast?: boolean },
  ): Observable<UpdateProfileResponse> {
    const safePayload = { ...payload };

    if (safePayload.phone == null || safePayload.phone === "") {
      delete safePayload.phone;
    }

    return this.http.put<UpdateProfileResponse>(
      `${environment.baseApiUrl}${this.API_ENDPOINT}`,
      safePayload,
      options?.suppressSuccessToast ? NoShowToastContext() : {},
    );
  }

  private patchOnboardingConfig(
    partial: Partial<OnboardingConfig>,
    options?: { suppressSuccessToast?: boolean },
  ): Observable<UserProfile> {
    const currentProfile = this.userProfile();

    return this.updateUserProfile(
      {
        name: currentProfile?.name,
        onboardingConfig: {
          ...(currentProfile?.onboardingConfig ?? {}),
          ...partial,
        },
      },
      options,
    );
  }
}
